// src/utils/sessionManager.js
import api from "../api/api";

const AUTH_KEYS = ["usuario", "sesssaoInicio"];

const SESSION_LOCAL_KEYS = ["userName", "estabelecimento_id", "estabelecimentoNome"];

const readAuthValue = (key) => {
  const fromSession = sessionStorage.getItem(key);
  if (fromSession !== null) return fromSession;

  const legacyKeys = key === "usuario" || key === "sesssaoInicio"
    ? [key]
    : [key, "token", "refreshToken"];

  for (const legacyKey of legacyKeys) {
    const legacy = localStorage.getItem(legacyKey);
    if (legacy !== null) {
      if (legacyKey === key) {
        sessionStorage.setItem(key, legacy);
      }
      localStorage.removeItem(legacyKey);
    }
  }

  return sessionStorage.getItem(key);
};

const writeAuthValue = (key, value) => {
  if (value === null || value === undefined) {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, value);
  localStorage.removeItem(key);
};

class SessionManager {
  constructor() {
    this.tokenRefreshInterval = null;
    this.REFRESH_INTERVAL = 10 * 60 * 1000;
    this.WARNING_TIME = 20 * 60 * 1000;
    this.expirationTimeout = null;
    this.activityListenersBound = false;
  }

  registrarAtividade() {
    if (!this.estaAutenticado()) return;

    writeAuthValue("sesssaoInicio", new Date().toISOString());

    if (this.expirationTimeout) {
      clearTimeout(this.expirationTimeout);
    }

    this.expirationTimeout = setTimeout(() => {
      this.limparSessao({ silent: true });
      window.dispatchEvent(new CustomEvent("session:ended", { detail: { reason: "expired" } }));
    }, this.WARNING_TIME);
  }

  bindActivityListeners() {
    if (this.activityListenersBound || typeof window === "undefined") return;

    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    const handler = () => this.registrarAtividade();

    events.forEach((eventName) => {
      window.addEventListener(eventName, handler, { passive: true });
    });

    this.activityListenersBound = true;
  }

  iniciar() {
    this.bindActivityListeners();
    const restored = this.restaurarSessao();
    this.agendarRenovacao();
    this.agendarVerificacaoExpiracao();

    try {
      if (restored) {
        this.registrarAtividade();
      }
    } catch (e) {
      console.error("[sessionManager] erro ao agendar timeout na inicialização:", e);
    }
  }

  restaurarSessao() {
    const usuario = readAuthValue("usuario");
    return Boolean(usuario);
  }

  salvarSessao(usuario) {
    writeAuthValue("usuario", JSON.stringify(usuario));
    writeAuthValue("sesssaoInicio", new Date().toISOString());
    writeAuthValue("token", null);
    writeAuthValue("refreshToken", null);
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");

    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }
    this.agendarRenovacao();
    this.registrarAtividade();

    try {
      window.dispatchEvent(new CustomEvent("session:started"));
    } catch (e) {
      // noop
    }
  }

  agendarRenovacao() {
    this.tokenRefreshInterval = setInterval(() => {
      this.renovarToken();
    }, this.REFRESH_INTERVAL);
  }

  agendarVerificacaoExpiracao() {
    setInterval(() => {
      if (!this.estaAutenticado()) return;

      const inicio = readAuthValue("sesssaoInicio");
      if (!inicio) return;

      const tempoDecorrido = new Date() - new Date(inicio);
      if (tempoDecorrido > this.WARNING_TIME) {
        if (this.expirationTimeout) {
          clearTimeout(this.expirationTimeout);
          this.expirationTimeout = null;
        }
        this.limparSessao({ silent: true });
        window.dispatchEvent(new CustomEvent("session:ended", { detail: { reason: "expired" } }));
      }
    }, 60 * 1000);
  }

  async renovarToken() {
    if (!this.estaAutenticado()) return false;

    try {
      await api.post("/auth/renovar");
      return true;
    } catch (erro) {
      console.error("Erro ao renovar token:", erro.message);
      if (erro.response?.status === 401) {
        this.limparSessao({ silent: true });
        window.dispatchEvent(new CustomEvent("session:ended", { detail: { reason: "refresh_expired" } }));
      }
      return false;
    }
  }

  async encerrarSessaoRemota() {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[sessionManager] Falha ao revogar sessão no servidor:", error?.message);
      }
    }
  }

  obterDestinoPosLogin(usuario = null) {
    const alvo = usuario || this.obterUsuario();
    return alvo?.precisa_trocar_senha ? "/primeiro-acesso" : "/dashboard";
  }

  async encerrarSessaoAtualSeExistir() {
    if (!this.estaAutenticado()) return;
    await this.encerrarSessaoRemota();
    this.limparSessao({ silent: true });
  }

  limparSessao({ silent = false } = {}) {
    AUTH_KEYS.forEach((key) => writeAuthValue(key, null));
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    SESSION_LOCAL_KEYS.forEach((key) => localStorage.removeItem(key));

    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }

    if (this.expirationTimeout) {
      clearTimeout(this.expirationTimeout);
      this.expirationTimeout = null;
    }

    if (!silent) {
      try {
        window.dispatchEvent(new CustomEvent("session:ended", { detail: { reason: "logout" } }));
      } catch (e) {
        // noop
      }
    }
  }

  estaAutenticado() {
    return Boolean(readAuthValue("usuario"));
  }

  obterUsuario() {
    const usuario = readAuthValue("usuario");
    return usuario ? JSON.parse(usuario) : null;
  }

  atualizarUsuario(usuario) {
    writeAuthValue("usuario", JSON.stringify(usuario));
  }

  obterToken() {
    return null;
  }

  obterRefreshToken() {
    return null;
  }

  obterTempoRestante() {
    const inicio = readAuthValue("sesssaoInicio");
    if (!inicio) return 0;

    const tempoRestante = this.WARNING_TIME - (new Date() - new Date(inicio));
    return tempoRestante > 0 ? tempoRestante : 0;
  }
}

export const sessionManager = new SessionManager();
